/**
 * SunoResultViewer — универсальный просмотрщик результата Suno-операции.
 *
 * Открывается после успешного завершения одной из действий, где у результата
 * есть полезное содержимое помимо "готово":
 *
 *   - stems       → 2 файла (вокал + инструментал) с плеерами и скачиванием
 *   - midi        → список инструментов с количеством нот
 *                   (данные из task.response_data, формат из гайда §10)
 *   - timestamped → караоке-режим: строки с таймкодами, подсветка текущей
 *                   при воспроизведении связанного трека
 *
 * Если backend не прислал ожидаемые поля (response_data пустой или в неожиданном
 * формате) — показываем graceful fallback «Готово, данные сохранены».
 *
 * Все три кейса собраны здесь сознательно — они все «результат sub-операции»
 * и близки по UX, а разделять на 3 компонента = тройное дублирование sheet-обвязки.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import MediaPlayer from '../../../components/MediaPlayer'
import { Icon } from '../../../../public/icons/Icon.tsx'
import {
	groupWordsIntoLines,
	activeLineIndex,
	activeWordIndex,
	type LyricLine,
	type AlignedWord,
} from '../utils/sunoTimestamped'
import { showToast } from '../../../components/Toast'
import type { TaskStatusResponse } from '../../../api/images'
import type { SunoActionKey } from '../hooks/useSunoTrackActions'
import type { SunoTrack } from '../../../api/music'
import { parseMidiResponseData, buildMidiFile, downloadMidi } from '../utils/sunoMidi'

interface Props {
	open: boolean
	onClose: () => void
	/** Ключ действия — определяет какой режим рендерить. */
	actionKey: SunoActionKey | null
	/** Полный task status от polling'а. */
	result: TaskStatusResponse | null
	/** Исходный трек (для timestamped-lyrics — нужен audio_url для плеера).
	 *  Остальные режимы его игнорируют. */
	sourceTrack?: SunoTrack | null
}

// ── MIDI types (из гайда §10) ──────────────────────────────────────
interface MidiInstrument {
	name?: string
	program?: number
	is_drum?: boolean
	notes?: Array<{ pitch: number; start: number; end: number; velocity?: number }>
}

// LyricLine / AlignedWord / groupWordsIntoLines — общие из
// ../utils/sunoTimestamped (раньше дублировались здесь; теперь DRY).

export default function SunoResultViewer({ open, onClose, actionKey, result, sourceTrack }: Props) {
	const title = (() => {
		switch (actionKey) {
			case 'stems': return 'Стемы готовы'
			case 'midi': return 'MIDI-данные'
			case 'timestamped': return 'Текст с таймкодами'
			case 'video': return 'Видеоклип готов'
			default: return 'Результат'
		}
	})()

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title={title}
			className="suno-result-viewer"
		>
			<div className="suno-result-viewer__body">
				{actionKey === 'stems' && <StemsView result={result} />}
				{actionKey === 'midi' && <MidiView result={result} />}
				{actionKey === 'timestamped' && (
					<TimestampedView result={result} sourceTrack={sourceTrack ?? null} />
				)}
				{actionKey === 'video' && (
					<VideoView result={result} sourceTrack={sourceTrack ?? null} />
				)}
				{!actionKey && <p>Нет данных.</p>}
			</div>
		</Sheet>
	)
}

// ───────────────────────────────────────────────────────────────────
// Stems — два плеера с download-кнопками
// ───────────────────────────────────────────────────────────────────
function StemsView({ result }: { result: TaskStatusResponse | null }) {
	const urls = result?.result_urls ?? (result?.result_url ? [result.result_url] : [])
	if (urls.length === 0) {
		return <p className="suno-result-viewer__empty">Стемы ещё не пришли от сервера.</p>
	}

	// По конвенции Suno: 1-й — вокал, 2-й — инструментал. Гайд §9.
	const labels = ['Вокал', 'Инструментал', 'Дорожка 3', 'Дорожка 4']

	const downloadFile = async (url: string, filename: string) => {
		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error(`HTTP ${response.status}`)
			const blob = await response.blob()
			const objectUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = objectUrl
			a.download = filename
			document.body.appendChild(a)
			a.click()
			a.remove()
			setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
		} catch {
			showToast('Не удалось скачать файл', 'error')
		}
	}

	return (
		<div className="suno-stems-view">
			{urls.map((url, i) => (
				<div key={i} className="suno-stems-view__item">
					<div className="suno-stems-view__label">
						<Icon id="gen-audio" size={14} />
						{labels[i] ?? `Дорожка ${i + 1}`}
					</div>
					<MediaPlayer
						variant="audio"
						src={url}
						title={labels[i] ?? `Дорожка ${i + 1}`}
						onDownload={() => downloadFile(url, `${labels[i] ?? `stem-${i + 1}`}.mp3`)}
					/>
				</div>
			))}
		</div>
	)
}

// ───────────────────────────────────────────────────────────────────
// MIDI — список инструментов + количество нот
// ───────────────────────────────────────────────────────────────────
function MidiView({ result }: { result: TaskStatusResponse | null }) {
	const instruments = useMemo<MidiInstrument[] | null>(
		() => parseMidiResponseData(result?.response_data),
		[result],
	)

	const handleDownload = () => {
		if (!instruments) return
		try {
			const bytes = buildMidiFile(instruments)
			if (!bytes) {
				showToast('Нет валидных нот для экспорта', 'error')
				return
			}
			downloadMidi(bytes, `suno-midi-${Date.now()}.mid`)
		} catch (err) {
			showToast(err instanceof Error ? err.message : 'Ошибка конвертации в .mid', 'error')
		}
	}

	if (!instruments || instruments.length === 0) {
		return (
			<div className="suno-result-viewer__fallback">
				<p>Готово — MIDI-данные сохранены на сервере.</p>
				<p className="suno-result-viewer__fallback-hint">
					Клиентский вьюер нот станет доступен как только backend начнёт
					отдавать <code>response_data</code> в task-статусе.
				</p>
			</div>
		)
	}

	const totalNotes = instruments.reduce((sum, inst) => sum + (inst.notes?.length ?? 0), 0)

	return (
		<div className="suno-midi-view">
			<div className="suno-midi-view__summary">
				<span>
					{instruments.length} инструмент{pluralRu(instruments.length, '', 'а', 'ов')} ·{' '}
					{totalNotes} нот{pluralRu(totalNotes, 'а', 'ы', '')}
				</span>
				<button
					type="button"
					className="suno-midi-view__download"
					onClick={handleDownload}
					disabled={totalNotes === 0}
					title="Скачать как стандартный MIDI-файл"
				>
					<Icon id="gen-download" size={12} />
					Скачать .mid
				</button>
			</div>
			<ul className="suno-midi-view__list">
				{instruments.map((inst, i) => (
					<li key={i} className="suno-midi-view__item">
						<span className="suno-midi-view__item-name">
							{inst.is_drum ? '🥁 ' : ''}
							{inst.name || `Инструмент ${i + 1}`}
						</span>
						<span className="suno-midi-view__item-count">
							{inst.notes?.length ?? 0} нот
						</span>
					</li>
				))}
			</ul>
		</div>
	)
}

function pluralRu(n: number, one: string, few: string, many: string): string {
	const mod10 = n % 10
	const mod100 = n % 100
	if (mod10 === 1 && mod100 !== 11) return one
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
	return many
}

// ───────────────────────────────────────────────────────────────────
// Timestamped lyrics — karaoke viewer
// ───────────────────────────────────────────────────────────────────
function TimestampedView({
	result,
	sourceTrack,
}: {
	result: TaskStatusResponse | null
	sourceTrack: SunoTrack | null
}) {
	const lines = useMemo<LyricLine[] | null>(() => {
		const data = result?.response_data as unknown
		if (!data || typeof data !== 'object') return null
		// Бэк сохраняет в {poll_number, raw: {...}} формате. Распаковываем.
		const wrapped = data as { raw?: unknown; poll_number?: number }
		const payload = wrapped.raw && typeof wrapped.raw === 'object' ? wrapped.raw : data

		// Suno raw-формат: массив слов с таймингами → группируем в строки.
		const p = payload as {
			aligned_words?: AlignedWord[]
			alignedWords?: AlignedWord[]
			lyrics?: LyricLine[]
			lines?: LyricLine[]
			segments?: LyricLine[]
		}
		const words = p.aligned_words || p.alignedWords
		if (Array.isArray(words) && words.length > 0 && typeof words[0].startS === 'number') {
			return groupWordsIntoLines(words)
		}

		// Legacy-форматы (если вдруг бэк отдаёт готовые строки).
		const arr = p.lyrics || p.lines || p.segments
		if (Array.isArray(arr) && arr.length > 0 && typeof arr[0].text === 'string') {
			return arr as LyricLine[]
		}
		return null
	}, [result])

	const audioUrl = sourceTrack?.audio_url || sourceTrack?.stream_audio_url || ''
	const audioRef = useRef<HTMLAudioElement>(null)
	const [currentTime, setCurrentTime] = useState(0)
	const activeLineRef = useRef<HTMLDivElement>(null)

	// Активная строка + «поющееся» слово (lines может быть null до данных).
	const activeIdx = lines ? activeLineIndex(lines, currentTime) : -1
	const activeWordIdx = lines ? activeWordIndex(lines[activeIdx], currentTime) : -1

	// Подписка на timeupdate — для подсветки текущей строки.
	useEffect(() => {
		const el = audioRef.current
		if (!el) return
		const onTime = () => setCurrentTime(el.currentTime)
		el.addEventListener('timeupdate', onTime)
		return () => el.removeEventListener('timeupdate', onTime)
	}, [audioUrl])

	// Автоскролл — только при СМЕНЕ активной строки (не на каждый тик:
	// иначе плавная прокрутка дёргается и «отстаёт» от слов).
	useEffect(() => {
		if (activeIdx < 0) return
		activeLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [activeIdx])

	// Spacebar = play/pause. Только когда фокус не в input/textarea — чтобы
	// не перехватывать пробел в полях ввода.
	useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.code !== 'Space') return
			const target = e.target as HTMLElement | null
			const tag = target?.tagName
			if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
			const el = audioRef.current
			if (!el || !el.src) return
			e.preventDefault()
			if (el.paused) el.play().catch(() => {})
			else el.pause()
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [])

	if (!lines) {
		return (
			<div className="suno-result-viewer__fallback">
				<p>Готово — таймированный текст сохранён на сервере.</p>
				<p className="suno-result-viewer__fallback-hint">
					Karaoke-режим активируется когда backend начнёт отдавать
					<code>response_data</code> с массивом строк и таймкодов.
				</p>
			</div>
		)
	}

	const handleLineClick = (line: LyricLine) => {
		const el = audioRef.current
		if (!el) return
		el.currentTime = line.start
		el.play().catch(() => {})
	}

	return (
		<div className="suno-timestamped-view">
			{audioUrl && (
				<>
					<audio
						ref={audioRef}
						src={audioUrl}
						controls
						preload="metadata"
						className="suno-timestamped-view__audio"
					/>
					<div className="suno-timestamped-view__hint">
						Пробел — play/pause · клик по строке — перейти к ней
					</div>
				</>
			)}
			<div className="suno-timestamped-view__lines" role="list">
				{lines.map((line, i) => {
					const isActive = i === activeIdx
					return (
						<div
							key={i}
							ref={isActive ? activeLineRef : null}
							role="listitem"
							className={`suno-timestamped-view__line${isActive ? ' suno-timestamped-view__line--active' : ''}`}
							onClick={() => handleLineClick(line)}
							title={`Перейти к ${line.start.toFixed(1)}с`}
						>
							<span className="suno-timestamped-view__line-time">
								{formatTime(line.start)}
							</span>
							<span className="suno-timestamped-view__line-text">
								{isActive && line.words?.length
									? line.words.map((w, wi) => (
										<span
											key={wi}
											className={`suno-timestamped-view__word${
												wi === activeWordIdx
													? ' suno-timestamped-view__word--current'
													: wi < activeWordIdx
														? ' suno-timestamped-view__word--sung'
														: ''
											}`}
										>
											{wi > 0 ? ' ' : ''}{w.word}
										</span>
									))
									: line.text}
							</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}

function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0:00'
	const mm = Math.floor(s / 60)
	const ss = Math.floor(s % 60)
	return `${mm}:${ss.toString().padStart(2, '0')}`
}

// ───────────────────────────────────────────────────────────────────
// Music video — плеер + кнопка скачать .mp4
// ───────────────────────────────────────────────────────────────────
function VideoView({
	result,
	sourceTrack,
}: {
	result: TaskStatusResponse | null
	sourceTrack: SunoTrack | null
}) {
	const videoUrl = result?.result_url || result?.result_urls?.[0] || ''

	const downloadVideo = async () => {
		if (!videoUrl) return
		try {
			const response = await fetch(videoUrl)
			if (!response.ok) throw new Error(`HTTP ${response.status}`)
			const blob = await response.blob()
			const objectUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = objectUrl
			const base = sourceTrack?.title?.trim() || 'suno-video'
			a.download = `${base}.mp4`
			document.body.appendChild(a)
			a.click()
			a.remove()
			setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
		} catch {
			showToast('Не удалось скачать видео', 'error')
		}
	}

	if (!videoUrl) {
		return (
			<div className="suno-result-viewer__fallback">
				<p>Видео готово, но URL не получен от сервера.</p>
				<p className="suno-result-viewer__fallback-hint">
					Попробуй обновить страницу через минуту — возможно ещё заливается.
				</p>
			</div>
		)
	}

	return (
		<div className="suno-video-view">
			<MediaPlayer
				variant="video"
				src={videoUrl}
				poster={sourceTrack?.image_url || undefined}
				className="suno-video-view__player-wrap"
				videoClassName="suno-video-view__player"
				onDownload={() => { void downloadVideo() }}
			/>

			<div className="suno-video-view__actions">
				<button
					type="button"
					className="suno-video-view__download"
					onClick={downloadVideo}
					title="Скачать видеоклип .mp4"
				>
					<Icon id="gen-download" size={14} />
					Скачать .mp4
				</button>
			</div>

			<p className="suno-video-view__hint">
				Музыкальный видеоклип сгенерирован Suno на основе трека. Длительность
				совпадает с длительностью аудио.
			</p>
		</div>
	)
}
